import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';

// Google Analytics Measurement ID - Replace with your own
const GA_MEASUREMENT_ID = window.ENV_GA_ID || 'G-XXXXXXXXXX';

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window === 'undefined') return;
  
  // Only initialize once
  if (window.gaInitialized) return;
  
  // Add gtag script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
  });

  window.gaInitialized = true;
};

// Track page views
export const trackPageView = (path) => {
  if (window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
    });
  }
};

// Track custom events
export const trackGAEvent = (action, category, label, value) => {
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Custom hook for analytics
export const useAnalytics = () => {
  const location = useLocation();

  // Initialize GA on mount
  useEffect(() => {
    initGA();
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname);
    
    // Also track to our custom backend
    trackEvent('page_view', { path: location.pathname }, location.pathname);
  }, [location.pathname]);

  // Track custom event to our backend
  const trackEvent = useCallback(async (eventType, eventData = {}, page = null) => {
    try {
      // Track to our backend
      await axios.post(`${API}/analytics/event`, {
        event_type: eventType,
        event_data: eventData,
        page: page || location.pathname,
      }).catch(() => {}); // Silently fail if not logged in

      // Also track to GA
      trackGAEvent(eventType, eventData.category || 'general', eventData.label || '', eventData.value);
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.debug('Analytics tracking failed:', error);
    }
  }, [location.pathname]);

  // Track monetization-specific events
  const trackMonetization = useCallback(async (eventType, data = {}) => {
    try {
      await axios.post(`${API}/monetization/event`, {
        event_type: eventType,
        variant: data.variant,
        trigger_source: data.triggerSource,
        feature_source: data.featureSource,
        plan_id: data.planId,
        billing_cycle: data.billingCycle,
        metadata: data.metadata,
      }).catch(() => {});
      
      // Also track to GA with monetization category
      trackGAEvent(eventType, 'monetization', data.triggerSource || '', data.amount);
    } catch (error) {
      console.debug('Monetization tracking failed:', error);
    }
  }, []);

  // Track paywall impression
  const trackPaywallImpression = useCallback(async (variant, triggerSource, featureSource = null) => {
    try {
      await axios.post(`${API}/monetization/paywall/impression`, {
        variant,
        trigger_source: triggerSource,
        feature_source: featureSource,
        page: location.pathname,
      }).catch(() => {});
      
      trackGAEvent('paywall_shown', 'monetization', triggerSource, null);
    } catch (error) {
      console.debug('Paywall impression tracking failed:', error);
    }
  }, [location.pathname]);

  // Track paywall dismiss
  const trackPaywallDismiss = useCallback(async (variant, triggerSource) => {
    try {
      await axios.post(`${API}/monetization/paywall/dismiss`, {
        variant,
        trigger_source: triggerSource,
        page: location.pathname,
      }).catch(() => {});
      
      trackGAEvent('paywall_dismissed', 'monetization', triggerSource, null);
    } catch (error) {
      console.debug('Paywall dismiss tracking failed:', error);
    }
  }, [location.pathname]);

  // Track conversion event
  const trackConversion = useCallback(async (eventType, planId, billingCycle, data = {}) => {
    try {
      await axios.post(`${API}/monetization/conversion`, {
        event_type: eventType,
        variant: data.variant,
        plan_id: planId,
        billing_cycle: billingCycle,
        trigger_source: data.triggerSource,
        feature_source: data.featureSource,
        amount: data.amount,
        currency: data.currency || 'USD',
      }).catch(() => {});
      
      trackGAEvent(eventType, 'conversion', planId, data.amount);
    } catch (error) {
      console.debug('Conversion tracking failed:', error);
    }
  }, []);

  return { 
    trackEvent, 
    trackMonetization, 
    trackPaywallImpression, 
    trackPaywallDismiss, 
    trackConversion 
  };
};

// Pre-defined event types for consistency
export const AnalyticsEvents = {
  // User actions
  SIGNUP: 'signup',
  LOGIN: 'login',
  LOGOUT: 'logout',
  
  // Item actions
  ITEM_CREATED: 'item_created',
  ITEM_COMPLETED: 'item_completed',
  ITEM_DELETED: 'item_deleted',
  PAST_TRIP_LOGGED: 'past_trip_logged',
  
  // Feature usage
  MAP_OPENED: 'map_opened',
  SUGGESTIONS_VIEWED: 'suggestions_viewed',
  BUDGET_OPENED: 'budget_opened',
  TRAVEL_SEARCH: 'travel_search',
  ACHIEVEMENT_VIEWED: 'achievement_viewed',
  
  // Social
  PARTNER_LINKED: 'partner_linked',
  GROUP_CREATED: 'group_created',
  GROUP_JOINED: 'group_joined',
  ITEM_SHARED: 'item_shared',
  
  // Engagement
  COMMENT_ADDED: 'comment_added',
  PHOTO_UPLOADED: 'photo_uploaded',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
  
  // Conversion (Monetization)
  UPGRADE_VIEWED: 'upgrade_viewed',
  CHECKOUT_STARTED: 'checkout_started',
  SUBSCRIPTION_PURCHASED: 'subscription_purchased',
  
  // Monetization funnel
  PAYWALL_SHOWN: 'paywall_shown',
  PAYWALL_DISMISSED: 'paywall_dismissed',
  PLAN_VIEWED: 'plan_viewed',
  MONTHLY_SELECTED: 'monthly_selected',
  ANNUAL_SELECTED: 'annual_selected',
  DUO_SELECTED: 'duo_selected',
  CTA_TAPPED: 'cta_tapped',
  TRIAL_CTA_TAPPED: 'trial_cta_tapped',
  RESTORE_TAPPED: 'restore_tapped',
  
  // Conversion events
  TRIAL_STARTED: 'trial_started',
  PURCHASE_COMPLETED: 'purchase_completed',
  PURCHASE_FAILED: 'purchase_failed',
  
  // Lifecycle events
  SUBSCRIPTION_RENEWED: 'subscription_renewed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  TRIAL_CONVERTED: 'trial_converted',
  
  // Upgrade/Downgrade
  EXPLORER_TO_DUO_UPGRADE: 'explorer_to_duo_upgrade',
  RESUBSCRIBE: 'resubscribe',
};

// Upgrade trigger features for attribution
export const UpgradeTriggers = {
  MAP_LAYERS: 'map_layers',
  MEMORY_VAULT: 'memory_vault',
  RANK_INSIGHTS: 'rank_insights',
  AI_PLANNER: 'ai_planner',
  ANNIVERSARY_VAULT: 'anniversary_vault',
  PARTNER_LINK_DUO: 'partner_link_duo_upsell',
  STREAK_PROTECTION: 'streak_protection',
  BUDGET_TRACKING: 'budget_tracking',
  PDF_EXPORT: 'pdf_export',
  ITEM_LIMIT: 'item_limit_exceeded',
  PHOTO_LIMIT: 'photo_limit_exceeded',
  OFFLINE_MODE: 'offline_mode',
};

export default useAnalytics;
